import React, { useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';
import {
  formatCouponExpiry,
  formatCouponOfferLabel,
  isCouponEligible,
} from './cartCouponUtils';

/**
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   coupons: import('./cartCouponUtils').NormalizedCoupon[],
 *   cartTotal: number,
 *   onApply: (coupon: import('./cartCouponUtils').NormalizedCoupon) => void,
 * }} props
 */
const CartCouponPickerModal = ({ open, onClose, coupons, cartTotal, onApply }) => {
  useEffect(() => {
    if (!open) return undefined;
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="cart-coupon-modal-overlay" onClick={onClose} role="presentation">
      <ModalPanel coupons={coupons} cartTotal={cartTotal} onClose={onClose} onApply={onApply} />
    </div>
  );
};

/** @param {{ coupons: import('./cartCouponUtils').NormalizedCoupon[], cartTotal: number, onClose: () => void, onApply: (c: import('./cartCouponUtils').NormalizedCoupon) => void }} props */
function ModalPanel({ coupons, cartTotal, onClose, onApply }) {
  return (
    <div
      className="cart-coupon-modal"
      onClick={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="cart-coupon-modal-title"
    >
      <div className="cart-coupon-modal__header">
        <h3 id="cart-coupon-modal-title">Available coupons</h3>
        <button type="button" className="cart-coupon-modal__close" onClick={onClose} aria-label="Close">
          <FaTimes />
        </button>
      </div>

      {coupons.length === 0 ? (
        <p className="cart-coupon-modal__empty">No coupons available right now. Please try again later.</p>
      ) : (
        <ul className="cart-coupon-modal__list">
          {coupons.map((coupon) => (
            <CouponListItem
              key={coupon.id || coupon.code}
              coupon={coupon}
              cartTotal={cartTotal}
              onApply={() => {
                onApply(coupon);
                onClose();
              }}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

/** @param {{ coupon: import('./cartCouponUtils').NormalizedCoupon, cartTotal: number, onApply: () => void }} props */
function CouponListItem({ coupon, cartTotal, onApply }) {
  const eligible = isCouponEligible(coupon, cartTotal);
  const expiry = formatCouponExpiry(coupon);

  return (
    <li className={`cart-coupon-modal__card${eligible ? '' : ' cart-coupon-modal__card--disabled'}`}>
      <p className="cart-coupon-modal__code">Code: {coupon.code}</p>
      <p className="cart-coupon-modal__offer">{formatCouponOfferLabel(coupon)}</p>
      {coupon.description ? <p className="cart-coupon-modal__desc">{coupon.description}</p> : null}
      {expiry ? <p className="cart-coupon-modal__expiry">Expires {expiry}</p> : null}
      {!eligible && (coupon.minOrderValue ?? 0) > 0 ? (
        <p className="cart-coupon-modal__min">
          Minimum order ₹{(coupon.minOrderValue ?? 0).toFixed(0)} (your cart ₹{cartTotal.toFixed(2)})
        </p>
      ) : null}
      <button
        type="button"
        className="cart-coupon-modal__apply"
        disabled={!eligible}
        onClick={() => {
          if (eligible) onApply();
        }}
      >
        Apply coupon
      </button>
    </li>
  );
}

export default CartCouponPickerModal;
